import React, { useCallback, useMemo } from 'react';
import { EntityForm } from '../form.types';
import {
  useQueryGetKiosk,
  useQueryGetKioskSettings,
  useQueryGetKioskDefaultSettings,
  useMutationUpdateKioskSettings
} from '../../../lockers/kiosks.queries';
import { useQueryGetHubDetails } from '../../../lockers/hubs.queries';
import { KioskNodeSubType, KioskSettingsForm } from './form';
import { KioskSettingsFormOutput } from './form-output';
import { kioskSettingsFormSchema } from './form-schema';
import { kioskSettingsFormUISchema } from './ui-schema';
import { KioskThemePreview, KioskThemePreviewButtonLabels } from '../kiosk-theme-form/kiosk-theme-preview.component';
import { KioskThemeSettingsFormOutput } from '../kiosk-theme-form/form-output';

export function useEditKioskForm(
  kioskID: string | undefined,
  displayLabel: string | undefined,
  projectID: string | undefined,
  subType: string | undefined,
  managerKioskID: string | undefined
): EntityForm | null {
  const isClient = subType === KioskNodeSubType.Client;
  const kioskResult = useQueryGetKiosk(kioskID);
  const settingsResult = useQueryGetKioskSettings(kioskID);
  // A client's own settings only hold what it overrides; inherited values are read from its manager.
  const managerSettingsResult = useQueryGetKioskSettings(isClient ? managerKioskID : undefined);
  const defaultSettingsResult = useQueryGetKioskDefaultSettings();
  const hubDetailsResult = useQueryGetHubDetails(kioskResult.data?.hubID);
  const updateMutation = useMutationUpdateKioskSettings();

  const defaultValues = useMemo((): KioskSettingsForm => {
    if (!settingsResult.data) return {} as KioskSettingsForm;
    if (isClient) {
      return KioskSettingsFormOutput.FromClientDto(
        settingsResult.data,
        managerSettingsResult.data,
        defaultSettingsResult.data
      );
    }
    return {
      ...KioskSettingsFormOutput.FromDto(settingsResult.data, defaultSettingsResult.data),
      iotHubId: kioskResult.data?.iotHubID ?? ''
    };
  }, [isClient, settingsResult.data, managerSettingsResult.data, kioskResult.data, defaultSettingsResult.data]);

  const initialFormOutput = useMemo(() => {
    return new KioskSettingsFormOutput(defaultValues);
  }, [defaultValues]);

  // Kiosk name is only meaningful on double-sided hubs (one kiosk per side); hide it for single-sided kiosks.
  const uiSchema = useMemo(() => {
    return kioskSettingsFormUISchema({
      showKioskName: hubDetailsResult.data?.isDoubleSided ?? true,
      stringDefaults: defaultSettingsResult.data?.strings,
      isClient
    });
  }, [hubDetailsResult.data, defaultSettingsResult.data, isClient]);

  const themeFormData = useMemo(
    () => (settingsResult.data ? KioskThemeSettingsFormOutput.FromDto(settingsResult.data) : {}),
    [settingsResult.data]
  );

  const previewTitle = useMemo(() => {
    const kiosk = kioskResult.data;
    if (!kiosk) return undefined;
    return kiosk.name ? `${kiosk.hubName} - ${kiosk.name}` : kiosk.hubName;
  }, [kioskResult.data]);

  const componentToRenderBelowForm = useCallback(
    (props: { formData: Record<string, any> }) => {
      const kioskForm = props.formData as KioskSettingsForm;
      const buttonLabels: KioskThemePreviewButtonLabels = {
        showInternal: kioskForm.showInternalUserButton === 'yes',
        internalLabel: kioskForm.internalUserButtonText || 'Residents',
        showExternal: kioskForm.showExternalUserButton === 'yes',
        externalLabel: kioskForm.externalUserButtonText || 'Couriers'
      };
      return React.createElement(KioskThemePreview, { formData: themeFormData, buttonLabels, title: previewTitle });
    },
    [themeFormData, previewTitle]
  );

  const onSubmit = useCallback(
    async (formData: Record<string, any>) => {
      if (!kioskID) return;
      const output = formData as KioskSettingsFormOutput;
      await updateMutation.mutateAsync({
        kioskID,
        dto: isClient
          ? KioskSettingsFormOutput.ToClientDto(output, settingsResult.data)
          : KioskSettingsFormOutput.ToDto(output, settingsResult.data, defaultSettingsResult.data),
        projectID
      });
    },
    [kioskID, projectID, isClient, settingsResult.data, defaultSettingsResult.data, updateMutation]
  );

  if (!kioskID) return null;

  return {
    // Wait for defaults too: default-matching strings must be blanked (placeholders) before first render.
    isLoading:
      settingsResult.isLoading ||
      kioskResult.isLoading ||
      defaultSettingsResult.isLoading ||
      managerSettingsResult.isLoading,
    title: displayLabel ?? 'Kiosk',
    subtitle: kioskID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema: kioskSettingsFormSchema,
    uiSchema,
    getOutput(formData) {
      return new KioskSettingsFormOutput(formData as KioskSettingsForm);
    },
    onSubmit,
    componentToRenderBelowForm,
    isLocked: false,
    lockedMessage: null
  };
}
